import React, { useState, useEffect, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthContext } from '../context/AuthContext'
import { courseService } from '../services/apiService'

const CreateCourse = () => {
  const { user } = useContext(AuthContext)
  const navigate = useNavigate()
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    price: '',
  })
  const [error, setError] = useState('')

  useEffect(() => {
    // Redirect if not an instructor
    if (user && user.role !== 'instructor') {
      navigate('/dashboard')
    }
    fetchCategories()
  }, [user, navigate])

  const fetchCategories = async () => {
    try {
      const response = await courseService.getCategories()
      const data = response.data.results || response.data
      setCategories(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const courseData = {
        title: formData.title,
        description: formData.description,
        category: parseInt(formData.category),
        price: parseFloat(formData.price),
      }

      const response = await courseService.createCourse(courseData)
      alert(
        'Course created successfully! Now you can add lessons, assignments, and quizzes.',
      )
      navigate(`/courses/${response.data.id}/manage`)
    } catch (err) {
      console.error('Error creating course:', err)
      setError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          'Failed to create course. Please try again.',
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
      <div className='bg-white rounded-lg shadow p-8'>
        <h1 className='text-3xl font-bold text-gray-900 mb-6'>
          Create New Course
        </h1>

        {error && (
          <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6'>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className='space-y-6'>
          <div>
            <label
              htmlFor='title'
              className='block text-sm font-medium text-gray-700 mb-2'
            >
              Course Title *
            </label>
            <input
              type='text'
              id='title'
              name='title'
              required
              value={formData.title}
              onChange={handleChange}
              className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
              placeholder='e.g., Introduction to Web Development'
            />
          </div>

          <div>
            <label
              htmlFor='description'
              className='block text-sm font-medium text-gray-700 mb-2'
            >
              Course Description *
            </label>
            <textarea
              id='description'
              name='description'
              required
              value={formData.description}
              onChange={handleChange}
              rows={6}
              className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
              placeholder='Describe what students will learn in this course...'
            />
          </div>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <div>
              <label
                htmlFor='category'
                className='block text-sm font-medium text-gray-700 mb-2'
              >
                Category *
              </label>
              <select
                id='category'
                name='category'
                required
                value={formData.category}
                onChange={handleChange}
                className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
              >
                <option value=''>Select a category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor='price'
                className='block text-sm font-medium text-gray-700 mb-2'
              >
                Price (USD) *
              </label>
              <input
                type='number'
                id='price'
                name='price'
                required
                min='0'
                step='0.01'
                value={formData.price}
                onChange={handleChange}
                className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
                placeholder='49.99'
              />
            </div>
          </div>

          <div className='flex items-center justify-between pt-4'>
            <button
              type='button'
              onClick={() => navigate('/my-courses')}
              className='px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={loading}
              className='px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50'
            >
              {loading ? 'Creating...' : 'Create Course'}
            </button>
          </div>
        </form>

        <div className='mt-8 p-4 bg-blue-50 rounded-md'>
          <h3 className='font-medium text-blue-900 mb-2'>Next Steps</h3>
          <p className='text-sm text-blue-800'>
            After creating your course, you'll be able to add lessons,
            assignments, and quizzes to provide a complete learning experience
            for your students.
          </p>
        </div>
      </div>
    </div>
  )
}

export default CreateCourse
